import React from 'react';
import ProductDto from '../Common/dtos/ProductDto';
import { RowFormText } from '../Common/Forms/FormText';
import EntityModal, { ModalMode } from '../Common/EntityModal';
import useUpsertProduct, { ProductUpsertDto } from '../../requests/useUpsertProduct';
import { FormSiteAssignment } from '../Common/SiteAssignment/FormSiteAssignment';
import useGetProductSiteAssignment from '../../requests/useGetProductSiteAssignment';
import useEntityForm from '../Common/useEntityForm';
import { RowUnitsSelect } from '../Units/UnitSelect';
import { UnitType } from '../Common/dtos/UnitDto';
import { Nav, NavItem, NavLink } from 'reactstrap';
import { FormRow } from '../Common/Forms/FormRow';

interface CreateEditProductProps {
  initialProduct: ProductDto | null;
  onDismiss: () => void;
  onUpsert: (productId: string, productName: string) => void;
  mode: ModalMode;
}

const defaultDensity = () => ({ value: 0, massUnit: 'Pound', volumeUnit: 'Gallon' });

const newProduct = (): ProductUpsertDto => ({
  name: '',
  epaNumber: '',
  ticketNotes: '',
  density: defaultDensity(),
  densityConstellationControlled: true,
});

export const CreateEditProduct = (props: CreateEditProductProps) => {
  const product = props.initialProduct ?? newProduct();
  const viewOnly = props.mode === ModalMode.View;

  const { hookProps, control, getValues, setValue, watch, submit, isSaving, loading, loadError, displayMessage, sites } =
    useEntityForm<ProductUpsertDto, ProductUpsertDto>({
      initialId: props.initialProduct?.id,
      defaultValues: {
        name: product.name,
        epaNumber: product.epaNumber,
        ticketNotes: product.ticketNotes,
        density: product.density,
        densityConstellationControlled: product.densityConstellationControlled,
        siteIds: [],
      },
      useSiteAssignment: options => useGetProductSiteAssignment(props.initialProduct?.id, options),
      useUpsert: options => useUpsertProduct(props.initialProduct?.id, options),
      toDto: form => ({
        ...form,
        density: form.densityConstellationControlled ? form.density : defaultDensity(),
      }),
      onSuccess: (productId, form) => {
        props.onDismiss();
        props.onUpsert(productId, form.name);
      },
    });

  const densityConstellationControlled = watch('densityConstellationControlled');

  return (<EntityModal
    mode={props.mode}
    entityName="product"
    displayMessage={displayMessage}
    loading={loading}
    loadError={loadError}
    renderSiteAssignment={<FormSiteAssignment {...hookProps('siteIds')} control={control} sites={sites} />}
    onHide={props.onDismiss}
    isSaving={isSaving}
    onSubmit={submit}
    renderForm={(<>
      <RowFormText displayName="Product Name"
        {...hookProps("name")}
        options={{
          required: 'Name is required',
          validate: {
            length: v => v.length < 51 || 'Name cannot be longer than 50 characters'
          }
        }}
      />
      <FormRow displayName="Density Source" {...hookProps("densityConstellationControlled")}
        control={<Nav tabs className="d-flex justify-content-center nav-justified">
            <NavItem>
              <NavLink active={!densityConstellationControlled} disabled={viewOnly}
                onClick={() => !viewOnly && setValue("densityConstellationControlled", false)}>
                TMX
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink active={densityConstellationControlled} disabled={viewOnly}
                onClick={() => !viewOnly && setValue("densityConstellationControlled", true)}>
                Constellation
              </NavLink>
            </NavItem>
          </Nav>}
      />
      {densityConstellationControlled && <>
        <RowFormText displayName='Density Value'
          {...hookProps("density.value")}
          options={{
            valueAsNumber: true,
            validate: {
              number:  v => !isNaN(Number(v)) || 'Must be a number',
              greaterThanOrEqualToZero: v => v >= 0 || 'Cannot be negative'
            }
          }}
        />
        <RowUnitsSelect id='density.massUnit' displayName='Density Mass Unit' unitType={UnitType.Mass} selectedValue={getValues("density.massUnit")}
          disabled={viewOnly} {...hookProps('density.massUnit')} />
        <RowUnitsSelect id='density.volumeUnit' displayName='Density Volume Unit' unitType={UnitType.Volume} selectedValue={getValues("density.volumeUnit")}
          disabled={viewOnly} {...hookProps('density.volumeUnit')} />
      </>}
      <RowFormText displayName='EPA Number' placeholder='EPA number'
        {...hookProps("epaNumber")}
        options={{
          validate: {
            length: v => v.length < 51 || 'EPA number cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText displayName="Ticket Notes"
        {...hookProps("ticketNotes")}
      />
    </>)} />);
}
